/**
 * THE THEME STORE (docs/plans/DARK-MODE.md §6.2). The person's appearance
 * choice (`preference`: light, the default, dark, or device) and what it
 * resolves to (`resolved`). Per device: the choice lives
 * in `localStorage['ee.theme']` (D4), never on the server, and a sign-in or
 * sign-out never touches it (D9).
 *
 * The inline boot script in index.html already painted the right theme
 * before React; this store takes over from there with the same rules
 * (utils/theme.ts) and keeps three things in step: the document's theme, the
 * theme-color meta, and the native chrome. "Match device" follows the OS
 * live, subscribed only while that is the choice. Every path follows the
 * choice (B8 lifted the v1 public-page pin, D6), so navigation changes
 * nothing here.
 */
import { create } from 'zustand';
import { applyNativeChrome } from '../utils/nativeChrome';
import {
  applyTheme,
  DARK_QUERY,
  parsePreference,
  prefersDark,
  type ResolvedTheme,
  readStoredTheme,
  resolveTheme,
  type ThemePreference,
  writeStoredTheme,
} from '../utils/theme';

interface ThemeState {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  /** Store a new choice on this device and apply it now. */
  setPreference: (preference: ThemePreference) => void;
  /** Re-resolve the current choice (the OS scheme changed). */
  resync: () => void;
}

function resolveFor(preference: ThemePreference): ResolvedTheme {
  return resolveTheme({ stored: preference, prefersDark: prefersDark() });
}

let deviceQuery: MediaQueryList | null = null;

function onDeviceChange(): void {
  useThemeStore.getState().resync();
}

/** Listen to the OS scheme only while the choice is "Match device". */
function followDevice(preference: ThemePreference): void {
  const want = preference === 'device';
  if (want && !deviceQuery) {
    try {
      deviceQuery = matchMedia(DARK_QUERY);
      deviceQuery.addEventListener('change', onDeviceChange);
    } catch {
      deviceQuery = null;
    }
  } else if (!want && deviceQuery) {
    deviceQuery.removeEventListener('change', onDeviceChange);
    deviceQuery = null;
  }
}

function commit(resolved: ResolvedTheme, previous: ResolvedTheme | null): void {
  applyTheme(resolved);
  if (resolved !== previous) void applyNativeChrome(resolved);
}

const initialPreference = parsePreference(readStoredTheme());

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: initialPreference,
  resolved: resolveFor(initialPreference),

  setPreference: (preference) => {
    const next = parsePreference(preference);
    writeStoredTheme(next);
    followDevice(next);
    const resolved = resolveFor(next);
    const previous = get().resolved;
    set({ preference: next, resolved });
    commit(resolved, previous);
  },

  resync: () => {
    const { preference, resolved: previous } = get();
    const resolved = resolveFor(preference);
    if (resolved !== previous) set({ resolved });
    commit(resolved, previous);
  },
}));

followDevice(initialPreference);
